import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MagnifyingGlassIcon, XIcon, ArrowRightIcon } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusPill from "@/components/StatusPill";
import ImportCsvDialog from "@/components/ImportCsvDialog";
import { useAuth } from "@/context/AuthContext";
import { listIngredients, getStats } from "@/lib/api";

const MOLECULE = "https://static.prod-images.emergentagent.com/jobs/a3737ece-de0e-4e41-a3b2-a63390e3fc2c/images/1dd3d23fef130743a6a104d6b4ab22d2ee238ff9b4825872a408b2996a8de3ff.png";

export default function Dashboard() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, prohibited: 0, restricted: 0, active: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({ cas: "", function: "", ingredientType: "all", status: "all", prohibited: "all" });
  const [refreshNonce, setRefreshNonce] = useState(0);
  const navigate = useNavigate();
  const { hasRole } = useAuth();

  const params = useMemo(() => {
    const p = {};
    if (q) p.q = q;
    if (filters.cas) p.cas = filters.cas;
    if (filters.function) p.function = filters.function;
    if (filters.ingredientType !== "all") p.ingredientType = filters.ingredientType;
    if (filters.status !== "all") p.status = filters.status;
    if (filters.prohibited !== "all") p.prohibited = filters.prohibited === "yes";
    return p;
  }, [q, filters]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listIngredients(params).then((d) => { if (!cancelled) { setItems(d); setLoading(false); } });
    return () => { cancelled = true; };
  }, [params, refreshNonce]);

  useEffect(() => { getStats().then(setStats).catch(() => {}); }, [items.length, refreshNonce]);

  const clearFilters = () => { setQ(""); setFilters({ cas: "", function: "", ingredientType: "all", status: "all", prohibited: "all" }); };
  const hasFilters = q || filters.cas || filters.function || filters.ingredientType !== "all" || filters.status !== "all" || filters.prohibited !== "all";

  return (
    <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 fade-in" data-testid="dashboard-page">
      {/* Hero */}
      <section className="grid grid-cols-1 lg:grid-cols-12 gap-8 border border-[var(--border)] p-8 md:p-10 bg-white">
        <div className="lg:col-span-7 flex flex-col justify-center">
          <span className="label-kicker" data-testid="hero-kicker">EU Regulation · 2025/1175</span>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-medium tracking-tighter mt-3">
            Cosmetic ingredient<br />compliance, precisely catalogued.
          </h1>
          <p className="mt-4 text-sm md:text-base text-[var(--text-muted)] max-w-xl leading-relaxed">
            Extract, store and display EU‑compliant cosmetic ingredient data.
            Built for regulatory affairs, CPSR authors and formulators.
          </p>
          <div className="flex items-center gap-2 mt-6">
            {hasRole("editor") && (
              <Button onClick={() => navigate("/new")} className="btn-primary rounded-none h-10 px-5 font-mono text-[11px] uppercase tracking-[0.18em]" data-testid="hero-new-btn">
                Add Ingredient <ArrowRightIcon size={14} weight="bold" className="ml-2" />
              </Button>
            )}
            {hasRole("admin") && <ImportCsvDialog onDone={() => setRefreshNonce((n) => n + 1)} />}
          </div>
        </div>
        <div className="lg:col-span-5 flex items-center justify-center">
          <img src={MOLECULE} alt="Molecule" className="max-h-[260px] object-contain" data-testid="hero-image" />
        </div>
      </section>

      {/* Stats */}
      <section className="grid grid-cols-2 md:grid-cols-4 mt-6 border border-[var(--border)] border-b-0" data-testid="stats-grid">
        {[
          { k: "TOTAL ENTRIES", v: stats.total, tid: "stat-total" },
          { k: "ACTIVE", v: stats.active, tid: "stat-active", color: "var(--success)" },
          { k: "RESTRICTED", v: stats.restricted, tid: "stat-restricted", color: "var(--warning)" },
          { k: "PROHIBITED", v: stats.prohibited, tid: "stat-prohibited", color: "var(--danger)" },
        ].map((s) => (
          <div key={s.k} className="p-6 border-b border-r border-[var(--border)] last:border-r-0" data-testid={s.tid}>
            <div className="label-kicker">{s.k}</div>
            <div className="flex items-baseline gap-2 mt-2">
              {s.color && <span className="status-dot" style={{ background: s.color }} />}
              <span className="font-heading text-3xl font-medium tracking-tight">{s.v}</span>
            </div>
          </div>
        ))}
      </section>

      {/* Filter Bar */}
      <section className="mt-6 border border-[var(--border)] bg-white">
        <div className="px-4 py-3 border-b border-[var(--border)] flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-1 min-w-[220px]">
            <MagnifyingGlassIcon size={16} className="text-[var(--text-muted)]" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search INCI / name / chemical…"
              className="rounded-none border-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-8 px-0 font-mono text-sm"
              data-testid="search-input"
            />
          </div>
          <Input
            value={filters.cas}
            onChange={(e) => setFilters({ ...filters, cas: e.target.value })}
            placeholder="CAS…"
            className="rounded-none h-9 w-[140px] font-mono text-xs"
            data-testid="filter-cas"
          />
          <Input
            value={filters.function}
            onChange={(e) => setFilters({ ...filters, function: e.target.value })}
            placeholder="Function (e.g. Anti‑aging)…"
            className="rounded-none h-9 w-[200px] font-mono text-xs"
            data-testid="filter-function"
          />
          <Select value={filters.ingredientType} onValueChange={(v) => setFilters({ ...filters, ingredientType: v })}>
            <SelectTrigger className="rounded-none h-9 w-[140px] font-mono text-xs" data-testid="filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="Natural">Natural</SelectItem>
              <SelectItem value="Vegan">Vegan</SelectItem>
              <SelectItem value="Veg">Veg</SelectItem>
              <SelectItem value="Synthetic">Synthetic</SelectItem>
              <SelectItem value="Biotech">Biotech</SelectItem>
              <SelectItem value="Mineral">Mineral</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger className="rounded-none h-9 w-[130px] font-mono text-xs" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="Active">Active</SelectItem>
              <SelectItem value="Restricted">Restricted</SelectItem>
              <SelectItem value="Banned">Banned</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.prohibited} onValueChange={(v) => setFilters({ ...filters, prohibited: v })}>
            <SelectTrigger className="rounded-none h-9 w-[140px] font-mono text-xs" data-testid="filter-prohibited"><SelectValue placeholder="Prohibited?" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any</SelectItem>
              <SelectItem value="yes">Prohibited · Yes</SelectItem>
              <SelectItem value="no">Prohibited · No</SelectItem>
            </SelectContent>
          </Select>
          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="rounded-none h-9 font-mono text-[11px] uppercase tracking-[0.14em]" data-testid="filter-clear">
              <XIcon size={12} className="mr-1" /> Clear
            </Button>
          )}
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ingredients-table">
            <thead>
              <tr className="text-left">
                {["INGREDIENT", "INCI (EU)", "CAS", "FUNCTION", "STATUS", "TYPES", ""].map((h, i) => (
                  <th key={i} className="label-kicker px-4 py-3 border-b border-[var(--border)] bg-[var(--surface)]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-[var(--text-muted)] font-mono text-xs">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-16 text-center text-[var(--text-muted)] font-mono text-xs" data-testid="empty-state">No ingredients match your filters.</td></tr>
              ) : items.map((ing) => (
                <tr key={ing.id} className="data-row cell-border cursor-pointer" onClick={() => navigate(`/ingredient/${ing.id}`)} data-testid={`row-${ing.id}`}>
                  <td className="px-4 py-3 font-heading font-medium tracking-tight">{ing.ingredientName}</td>
                  <td className="px-4 py-3 font-mono text-xs">{ing.inciEU || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs">{ing.casNumber || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs text-[var(--text-muted)]">{(ing.function || []).slice(0, 2).join(", ")}{(ing.function || []).length > 2 ? "…" : ""}</td>
                  <td className="px-4 py-3"><StatusPill status={ing.status} prohibited={ing.prohibited} /></td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(ing.ingredientTypes || []).slice(0, 3).map((t, i) => (<span key={i} className="chip">{t}</span>))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/ingredient/${ing.id}`} className="text-[var(--primary)] text-xs font-mono uppercase tracking-[0.14em] hover:underline" data-testid={`row-open-${ing.id}`} onClick={(e) => e.stopPropagation()}>
                      Open →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}
